<template>
  <div class="landing">
    <div class="hero">
      <div class="hero-inner">
        <div class="hero-text">
          <div class="wechat-logo">微</div>
          <h1 class="hero-title">微信支付</h1>
          <p class="hero-sub">请根据以下信息完成转账，或扫码系统将自动核验到账</p>
        </div>
      </div>
    </div>

    <div class="content">
      <div v-if="loading">
        <div class="card">
          <div class="card-body center loading-wrap">
            <div class="loader"></div>
            <p class="loading-text">正在加载支付信息...</p>
          </div>
        </div>
      </div>

      <div v-else-if="error">
        <div class="card">
          <div class="card-body center">
            <span class="badge">提示</span>
            <p class="error-text">{{ error }}</p>
          </div>
        </div>
      </div>

      <div v-else-if="paid">
        <div class="card">
          <div class="card-body center">
            <div class="success-icon">✅</div>
            <h3 class="result-title">订单已支付</h3>
            <p class="result-sub">请勿重复转账</p>
          </div>
        </div>
      </div>

      <div v-else-if="expired">
        <div class="card">
          <div class="card-body center">
            <div class="timeout-icon">⏰</div>
            <h3 class="result-title">订单已超时</h3>
            <p class="result-sub">请返回并重新发起支付</p>
          </div>
        </div>
      </div>

      <div v-else class="row">
        <div class="col-left">
          <div class="card info-card">
            <div class="card-header info-header">
              <span>订单支付信息</span>
              <span class="countdown" v-if="countdownSeconds > 0">
                剩余支付时间：
                <span class="countdown-time">{{ formattedCountdown }}</span>
              </span>
              <span class="countdown ended" v-else>支付倒计时已结束</span>
            </div>
            <div class="card-body">
              <div class="form">
                <div
                  class="form-item"
                  v-for="(value, key) in infoMap"
                  :key="key"
                  :class="{ 'amount-item': key === 'paymentAmount' }"
                >
                  <label class="form-label">{{ value.label }}</label>
                  <div class="input-block field-row">
                    <span
                      class="field-value"
                      :class="{ mono: key === 'payerAccount', amount: key === 'paymentAmount' }"
                    >
                      {{ data[value.key] }}
                    </span>
                    <button class="copy-btn" @click="copyText(data[value.key])">
                      复制
                    </button>
                  </div>
                </div>
              </div>
              <div class="tips urgent-tip">
                <span class="tip-icon">📌</span>
                如果扫码不成功，请尝试转账给手机号。
              </div>
              <div class="transfer-guide">
                <p class="transfer-guide-title">转账方法：</p>
                <p class="transfer-guide-line">【微信首页右上角+】→【收付款】→</p>
                <p class="transfer-guide-line">【向银行卡或手机号转账】→【向手机号转账】→</p>
                <p class="transfer-guide-line">输入手机号和金额</p>
              </div>
              <div v-if="showQRCode" class="qrcode-section">
                <div class="qrcode-title">扫码支付</div>
                <div class="qrcode-wrapper">
                  <div id="qrcode" ref="qrcode" title=""></div>
                </div>
                <p class="qrcode-tip">使用微信扫描上方二维码。</p>
                <ul class="qrcode-tip qrcode-notice">
                  <li>1. 请在倒计时内完成支付。</li>
                  <li>2. 请按订单金额进行支付。</li>
                  <li>3. 请不要保存二维码再次支付。</li>
                  <li>否则将无法实时到账，产生的损失由汇款方承担。</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="col-right">
          <div class="card side-card">
            <div class="card-header">温馨提示</div>
            <div class="card-body">
              <ul class="timeline">
                <li class="timeline-item">
                  <span class="timeline-icon">⏰</span>
                  <div class="timeline-content">
                    <h3 class="timeline-title">到账时间</h3>
                    <p>微信转账一般实时到账，高峰时段可能会有短暂延迟。</p>
                  </div>
                </li>
                <li class="timeline-item">
                  <span class="timeline-icon">💰</span>
                  <div class="timeline-content">
                    <h3 class="timeline-title">金额一致</h3>
                    <p>请按页面金额转账，若不一致可能无法自动匹配。</p>
                  </div>
                </li>
                <li class="timeline-item">
                  <span class="timeline-icon">🔒</span>
                  <div class="timeline-content">
                    <h3 class="timeline-title">防骗提醒</h3>
                    <p>谨防冒充微信客服的电话或短信，不要透露任何验证码。</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import apiConfig from "@/config/api";
import QRCode from "qrcodejs2";

export default {
  name: "WechatTransfer",
  data() {
    return {
      data: {},
      loading: true,
      error: null,
      countdownSeconds: 0,
      countdownTimerId: null,
      expired: false,
      paid: false,
      qrcodeInstance: null,
      showQRCode: false,
      infoMap: {
        merchantOrderNo: { label: "订单号", key: "merchantOrderNo" },
        paymentAmount: { label: "支付金额", key: "paymentAmount" },
        payerName: { label: "收款人", key: "payerName" },
        payerAccount: { label: "手机号码", key: "payerAccount" }
      }
    };
  },
  methods: {
    copyText(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      if (window.layui?.layer) {
        window.layui.layer.msg("已复制到剪贴板", { icon: 1, time: 1500 });
      } else {
        alert("✅ 已复制到剪贴板");
      }
    },
    startCountdown(totalSeconds) {
      this.clearCountdown();
      this.countdownSeconds = totalSeconds;
      this.countdownTimerId = setInterval(() => {
        if (this.countdownSeconds > 0) {
          this.countdownSeconds--;
          if (this.countdownSeconds === 0) {
            this.onCountdownEnd();
          }
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimerId) {
        clearInterval(this.countdownTimerId);
        this.countdownTimerId = null;
      }
    },
    onCountdownEnd() {
      this.expired = true;
    },
    initQRCode(qrCodeBase64) {
      if (!qrCodeBase64) {
        return;
      }
      try {
        const qrCodeUrl = atob(qrCodeBase64);
        this.showQRCode = true;
        this.$nextTick(() => {
          setTimeout(() => {
            if (!this.$refs.qrcode) {
              return;
            }
            if (this.qrcodeInstance) {
              this.$refs.qrcode.innerHTML = "";
              this.qrcodeInstance = null;
            }
            this.qrcodeInstance = new QRCode(this.$refs.qrcode, {
              text: qrCodeUrl,
              width: 200,
              height: 200,
              colorDark: "#111111",
              colorLight: "#ffffff",
              correctLevel: QRCode.CorrectLevel.H
            });

            this.$nextTick(() => {
              const qrcodeElement = this.$refs.qrcode;
              if (!qrcodeElement) return;
              qrcodeElement.removeAttribute("title");
              qrcodeElement.setAttribute("title", "");
              const links = qrcodeElement.querySelectorAll("a");
              links.forEach((link) => {
                link.removeAttribute("title");
                const parent = link.parentNode;
                while (link.firstChild) {
                  parent.insertBefore(link.firstChild, link);
                }
                parent.removeChild(link);
              });
              const allElements = qrcodeElement.querySelectorAll("*");
              allElements.forEach((el) => {
                el.removeAttribute("title");
                if (el.tagName === "CANVAS" || el.tagName === "IMG") {
                  el.style.pointerEvents = "none";
                }
                if (el.tagName === "IMG") {
                  el.removeAttribute("alt");
                }
              });
              const canvas = qrcodeElement.querySelector("canvas");
              const img = qrcodeElement.querySelector("img");
              if (canvas) {
                canvas.removeAttribute("title");
                canvas.setAttribute("title", "");
                canvas.style.pointerEvents = "none";
              }
              if (img) {
                img.removeAttribute("title");
                img.setAttribute("title", "");
                img.removeAttribute("alt");
                img.style.pointerEvents = "none";
              }
              qrcodeElement.style.pointerEvents = "none";
            });
          }, 100);
        });
      } catch (error) {
        console.error("二维码生成失败：", error);
      }
    }
  },
  created() {
    const merchantOrderNo = this.$route.params.merchantOrderNo;
    const qrCodeFromQuery = this.$route.query.qrCode;
    if (qrCodeFromQuery) {
      this.initQRCode(qrCodeFromQuery);
    }

    axios
      .get(apiConfig.getCommonInterfaceUrl(), {
        params: { merchantOrderNo }
      })
      .then((res) => {
        if (res.data.code === 200) {
          this.data = res.data.data;
          if (Number(this.data.order_status) === -1) {
            this.expired = true;
          } else {
            const expireTime = new Date(this.data.expire_time).getTime();
            const nowTime = Date.now();
            const diffSeconds = Math.floor((expireTime - nowTime) / 1000);
            if (diffSeconds > 0) {
              this.startCountdown(diffSeconds);
            } else {
              this.expired = true;
            }
          }

          if (!this.$route.query.qrCode && res.data.data.payCode) {
            this.initQRCode(res.data.data.payCode);
          }
        } else {
          this.error = res.data.msg || "订单获取失败";
        }
      })
      .catch(() => {
        this.error = "请求失败，无法获取订单信息";
      })
      .finally(() => {
        this.loading = false;
      });
  },
  computed: {
    formattedCountdown() {
      const mins = Math.floor(this.countdownSeconds / 60);
      const secs = this.countdownSeconds % 60;
      return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
  },
  beforeDestroy() {
    this.clearCountdown();
    if (this.qrcodeInstance && this.$refs.qrcode) {
      this.$refs.qrcode.innerHTML = "";
      this.qrcodeInstance = null;
    }
  }
};
</script>

<style scoped>
.landing {
  background-color: #f4f7f5;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif;
}

.hero {
  background: linear-gradient(135deg, #07c160 0%, #05a94f 100%);
  color: #fff;
  padding: 60px 0 40px;
  text-align: center;
}

.hero-inner {
  max-width: 980px;
  margin: 0 auto;
  padding: 0 22px;
}

.hero-text {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.wechat-logo {
  width: 80px;
  height: 80px;
  background: #07c160;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 42px;
  font-weight: 700;
  color: #fff;
}

.hero-title {
  font-size: 48px;
  font-weight: 600;
  margin: 0 0 12px;
  letter-spacing: -0.015em;
  color: #fff;
}

.hero-sub {
  font-size: 21px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.content {
  margin: 40px auto 80px;
  max-width: 980px;
  padding: 0 22px;
}

.card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.08);
  border: 1px solid #edf2ee;
  overflow: hidden;
  margin-bottom: 24px;
}

.card-header {
  padding: 28px 28px 0;
  font-size: 17px;
  font-weight: 600;
  color: #1f1f1f;
}

.info-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.countdown {
  font-size: 14px;
  color: #07c160;
}

.countdown-time {
  color: #fa5151;
  font-weight: 600;
}

.countdown.ended {
  color: #fa5151;
}

.card-body {
  padding: 0 28px 28px;
}

.row {
  display: flex;
  gap: 30px;
}

.col-left {
  flex: 0 0 58.333333%;
  max-width: 58.333333%;
}

.col-right {
  flex: 0 0 41.666667%;
  max-width: 41.666667%;
}

.form-item {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #edf2ee;
  display: flex;
  align-items: center;
}

.form-item:first-child {
  padding-top: 8px;
}

.form-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.form-label {
  font-size: 15px;
  color: #7a7a7a;
  width: 90px;
  min-width: 90px;
  margin-right: 8px;
}

.input-block {
  flex: 1;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.field-value {
  font-size: 17px;
  color: #1f1f1f;
  flex: 1;
  min-width: 100px;
  word-break: break-all;
  line-height: 22px;
}

.field-value.mono {
  font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace;
}

.copy-btn {
  min-width: 80px;
  background: #07c160;
  border: none;
  border-radius: 999px;
  color: #ffffff;
  font-size: 14px;
  padding: 6px 16px;
  line-height: 22px;
  height: 34px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.copy-btn:hover {
  background: #05a94f;
}

.tips {
  margin-top: 24px;
  padding: 16px;
  border-radius: 12px;
  background: #f0f9f3;
  color: #5f5f5f;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.urgent-tip {
  color: #fa5151;
  font-weight: 600;
  font-size: 15px;
}

.transfer-guide {
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f7faf8;
  border: 1px solid #edf2ee;
}

.transfer-guide-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f1f1f;
}

.transfer-guide-line {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: #444;
  word-break: break-all;
}

.transfer-guide-line + .transfer-guide-line {
  margin-top: 4px;
}

.tip-icon {
  color: #07c160;
}

.qrcode-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #edf2ee;
  text-align: center;
}

.qrcode-title {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 12px;
}

.qrcode-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #edf2ee;
  margin-bottom: 16px;
}

#qrcode {
  display: inline-block;
  pointer-events: none;
  user-select: none;
}

#qrcode canvas,
#qrcode img,
#qrcode a {
  pointer-events: none;
}

.qrcode-tip {
  font-size: 14px;
  color: #6b6b6b;
  margin: 0;
}

.qrcode-notice {
  margin-top: 8px;
  padding-left: 0;
  list-style: none;
  text-align: left;
}

.qrcode-notice li {
  margin-top: 4px;
  line-height: 1.6;
  color: #fa5151;
}

.center {
  text-align: center;
}

.loading-wrap {
  padding: 60px 0;
}

.loading-text {
  color: #7a7a7a;
  font-size: 17px;
  margin-top: 16px;
}

.error-text {
  color: #fa5151;
  margin-top: 16px;
  font-size: 17px;
}

.success-icon,
.timeout-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.result-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px;
}

.result-sub {
  color: #666;
  margin: 0;
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  background: #07c160;
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 8px;
}

.loader {
  width: 40px;
  height: 40px;
  border: 3px solid #edf2ee;
  border-top-color: #07c160;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.side-card .timeline {
  padding: 0;
  list-style: none;
  margin: 0;
}

.side-card .timeline-item {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #edf2ee;
  display: flex;
  align-items: flex-start;
}

.side-card .timeline-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.timeline-icon {
  font-size: 20px;
  flex-shrink: 0;
  margin-right: 12px;
  margin-top: 2px;
}

.timeline-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f1f1f;
  margin: 0 0 6px;
}

.timeline-content p {
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .hero {
    padding: 40px 0 24px;
  }

  .hero-title {
    font-size: 28px;
  }

  .hero-sub {
    font-size: 15px;
  }

  .content {
    margin: 24px auto 0;
    padding: 0 20px;
    padding-bottom: max(40px, env(safe-area-inset-bottom) + 20px);
  }

  .row {
    flex-direction: column;
    gap: 0;
  }

  .col-left,
  .col-right {
    flex: 1;
    max-width: 100%;
  }

  .card-header {
    padding: 16px 16px 0;
    font-size: 15px;
  }

  .card-body {
    padding: 0 16px 16px;
  }

  .form-item {
    margin-bottom: 12px;
    padding-bottom: 12px;
  }
}
</style>
